import React, { useEffect, useState, useRef } from "react";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import { HandleGridBody } from "components/common/HandleGridBody.js";
import {
  AmountFormatCell,
  DateFormatCell,
  formatDateForAPI,
} from "components/common/formatter.js";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton.jsx";
import { handleApiError } from "Helper/index.js";
import { arrowRotateCwIcon } from "@progress/kendo-svg-icons";
import { useActiveSuppliers } from "services/supplierService.js";
import { useCompany } from "services/companyService.js";
import MattersCombobox from "../Postings/MattersComboBox.jsx";
import { GridLoader } from "components/GridLoader.jsx";
import PaySupplier from "pages/PaySupplier/PaySupplier";
import * as api from "../../api/index.js";
import { GRID_PAGE_SIZES } from "components/common/GridComponents";

const TransactionTypeOptions = [
  { label: "All", value: null },
  { label: "Type 0", value: 0 },
  { label: "Type 1", value: 1 },
  { label: "Type 2", value: 2 },
];

const CreditorsManagement = () => {
  const date = new Date();
  const [refresh, setRefresh] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(new Date()?.toISOString().split("T")[0]);
  const [fromDateSelector, setFromDateSelector] = useState(date);
  const [toDateSelector, setToDateSelector] = useState(date);
  const [valueFrom, setValueFrom] = useState(false);
  const [valueTo, setValueTo] = useState(false);
  const [matterref, setmatterref] = useState(null);
  const [supplierValue, setSupplierValue] = useState(null);
  const [payWhenPaid, setPayWhenPaid] = useState(false);
  const [transactionType, setTransactionType] = useState(TransactionTypeOptions[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [list, setList] = useState({ data: [], total: 0 });
  const [dataState, setDataState] = useState({
    take: 100,
    skip: 0,
    sort: [{ field: "matterRef", dir: "asc" }],
  });
  const [checkedList, setCheckedList] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showPaySupplier, setShowPaySupplier] = useState(false);
  const [selectedSupplierRef, setSelectedSupplierRef] = useState(null);

  const { data: suppliersdata } = useActiveSuppliers();
  const { data: company } = useCompany();
  const isRequestInProgress = useRef(false);

  useEffect(() => {
    if (company.yearStartDate !== undefined) {
      let yearsToStartDate = new Date(company.yearStartDate);
      setFromDateSelector(yearsToStartDate);
      setFromDate(formatDateForAPI(yearsToStartDate));
    }
  }, [company.yearStartDate]);

  useEffect(() => {
    fetchUnpaidDisbursements();
  }, [
    refresh,
    dataState,
    matterref,
    supplierValue,
    valueFrom,
    valueTo,
    fromDate,
    toDate,
    payWhenPaid,
    transactionType,
  ]);

  const fetchUnpaidDisbursements = async () => {
    if (isRequestInProgress.current) return;
    isRequestInProgress.current = true;
    setIsLoading(true);

    try {
      const params = {
        Page: Math.floor(dataState.skip / dataState.take) + 1,
        Limit: dataState.take,
      };

      if (matterref?.matterId) params.MatterId = matterref.matterId;
      if (supplierValue?.supplierRef) {
        params.SupplierRef = supplierValue.supplierRef;
      }
      if (valueFrom && fromDate) params.FromDate = fromDate;
      if (valueTo && toDate) params.ToDate = toDate;
      if (payWhenPaid) {
        params.PayWhenPaid = true;
      }
      if (transactionType?.value !== null && transactionType?.value !== undefined) {
        params.TransactionType = transactionType.value;
      }
      if (dataState.sort && dataState.sort.length > 0) {
        const sortField = dataState.sort[0].field;
        const sortDir = dataState.sort[0].dir === "asc" ? "" : " desc";
        params.OrderBy = `${sortField}${sortDir}`;
      }

      const response = await api.getUnpaidDisbursements(params);
      
      const dataWithSelect = (response?.data?.data || []).map(item => ({
        ...item,
        select: false,
      }));
      
      setList({
        data: dataWithSelect,
        total: response?.data?.total || 0,
      });
      setCheckedList([]);
      setSelectAll(false);
    } catch (error) {
      handleApiError(error);
      setList({ data: [], total: 0 });
    } finally {
      setIsLoading(false);
      isRequestInProgress.current = false;
    }
  };

  const onChangeFromDateSelect = (event) => {
    setFromDateSelector(event.value);
    let fromDate = null;
    let date = new Date(event.value);
    if (event.value) {
      fromDate = formatDateForAPI(date);
    }
    setFromDate(fromDate);
    setValueFrom(true);
  };

  const onChangeToDateSelect = (event) => {
    setToDateSelector(event.value);
    let toDate = null;
    let date = new Date(event.value);
    if (event.value) {
      toDate = formatDateForAPI(date);
    }
    setToDate(toDate);
    setValueTo(true);
  };

  const onChangematter = (event) => {
    if (event.target.value !== null && event.target.value !== undefined) {
      setmatterref(event?.target?.value);
    } else {
      setmatterref(null);
    }
  };

  const handleRefresh = () => {
    setRefresh((prev) => !prev);
  };

  const dataStateChange = (e) => {
    if (
      e.dataState.sort !== undefined &&
      e.dataState.skip !== 0 &&
      dataState.sort !== e.dataState.sort
    ) {
      setDataState({ ...e.dataState, skip: 0 });
    } else if (e.dataState.sort[0] === undefined) {
      setDataState({
        ...e.dataState,
        sort: [{ field: dataState.sort[0].field, dir: "asc" }],
      });
    } else {
      setDataState(e.dataState);
    }
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    setList((prevList) => {
      if (newSelectAll) {
        // When selecting all, check if all items have the same supplier
        const firstSupplier = prevList.data[0]?.supplierRef;
        const allSameSupplier = prevList.data.every(
          (item) => item.supplierRef === firstSupplier
        );

        if (!allSameSupplier) {
          // If mixed suppliers, only select items with the first supplier
          const updatedData = prevList.data.map((item) => ({
            ...item,
            select: item.supplierRef === firstSupplier,
          }));
          
          setCheckedList(
            updatedData.filter(item => item.select).map((item) => item.postingId)
          );
          setSelectedSupplierRef(firstSupplier);
          
          return { ...prevList, data: updatedData };
        } else {
          // All same supplier, select all
          const updatedData = prevList.data.map((item) => ({
            ...item,
            select: true,
          }));
          
          setCheckedList(updatedData.map((item) => item.postingId));
          setSelectedSupplierRef(firstSupplier);
          
          return { ...prevList, data: updatedData };
        }
      } else {
        // Deselect all
        const updatedData = prevList.data.map((item) => ({
          ...item,
          select: false,
        }));
        
        setCheckedList([]);
        setSelectedSupplierRef(null);
        
        return { ...prevList, data: updatedData };
      }
    });
  };

  const SelectHeaderCell = () => {
    return (
      <td className="text-center align-middle">
        <Checkbox checked={selectAll} className="ms-2" onChange={handleSelectAll} />
      </td>
    );
  };

  const SelectCell = (props) => {
    return (
      <td className="text-center align-middle">
        <Checkbox
          type="checkbox"
          checked={props.dataItem.select === true}
          onChange={() => {
            const itemSupplierRef = props.dataItem.supplierRef;
            const isCurrentlySelected = props.dataItem.select;
            
            setList((prevList) => {
              // If selecting and there's already a different supplier selected, clear all selections
              if (!isCurrentlySelected && selectedSupplierRef && selectedSupplierRef !== itemSupplierRef) {
                // Clear all previous selections
                const clearedData = prevList.data.map((item) => ({
                  ...item,
                  select: false,
                }));
                setCheckedList([]);
                setSelectAll(false);
                setSelectedSupplierRef(null);
                
                // Then select the new item
                const newData = clearedData.map((item) => ({
                  ...item,
                  select: item.postingId === props.dataItem.postingId,
                }));
                
                setCheckedList([props.dataItem.postingId]);
                setSelectedSupplierRef(itemSupplierRef);
                return { ...prevList, data: newData };
              }
              
              // Normal toggle behavior
              const updatedData = prevList.data.map((item) => {
                if (item.postingId === props.dataItem.postingId) {
                  return { ...item, select: !item.select };
                }
                return item;
              });

              // Update checked list
              const newCheckedList = updatedData
                .filter((item) => item.select)
                .map((item) => item.postingId);
              
              setCheckedList(newCheckedList);
              
              // Update supplier ref
              if (newCheckedList.length === 0) {
                setSelectedSupplierRef(null);
              } else if (!isCurrentlySelected) {
                setSelectedSupplierRef(itemSupplierRef);
              }
              
              // Update select all
              setSelectAll(updatedData.length > 0 && updatedData.every((item) => item.select));
              
              return { ...prevList, data: updatedData };
            });
          }}
        />
      </td>
    );
  };


  const getSelectedData = () => {
    return list.data.filter((item) => item.select === true).map((item) => ({
      ...item,
      inEdit: true,
      add: true,
      amount: item.amountDue || 0,
    }));
  };

  const getSelectedSupplier = () => {
    const selectedItems = list.data.filter((item) => item.select === true);
    if (selectedItems.length > 0) {
      const firstItem = selectedItems[0];
      return {
        supplierRef: firstItem.supplierRef,
        supplierName: firstItem.supplierName,
        display: `${firstItem.supplierRef} - ${firstItem.supplierName}`,
      };
    }
    return null;
  };

  return (
    <div className="page h-100 d-flex flex-column">
      <div className="d-flex justify-content-between flex-wrap mb-3">
        <div className="d-flex">
          <div className="d-flex flex-column me-3 mb-1">
            <Label className="mb-2 filter-font">Supplier</Label>
            <ComboBox
              data={suppliersdata}
              value={supplierValue}
              onChange={(e) => setSupplierValue(e.value)}
              textField="display"
              dataItemKey="supplierRef"
              placeholder="Select Supplier"
              className="dropdown-width"
              size={"small"}
              filterable={true}
            />
          </div>

          <div className="d-flex flex-column me-3 mb-1">
            <Label className="mb-2 filter-font">Matter</Label>
            <MattersCombobox
              onChangematter={onChangematter}
              className="dropdown-width"
              value={matterref}
              style={{ minWidth: "225px" }}
            />
          </div>

          <div className="me-2 mt-1">
            <div className="nominal-form-to-parent-row">
              <div className="labels-containers">
                <Label editorId={"dp"}>From</Label>
                <div className="label-to">
                  <Label editorId="to">To</Label>
                </div>
              </div>
            </div>
            <div className="nominal-form-to-row">
              <div className="date-form-to-wrapper" style={{ height: "40px" }}>
                <div className="from">
                  <div className="d-flex align-items-center justify-content-center">
                    <Checkbox
                      value={valueFrom}
                      className="ms-2 me-1"
                      onChange={(e) => {
                        setValueFrom(e.value);
                      }}
                      label={""}
                    />
                    <DatePicker
                      id="from"
                      className="date-picker"
                      value={fromDateSelector || null}
                      onChange={onChangeFromDateSelect}
                      toggleButton={CalenderCustomToggleButton}
                    />
                  </div>
                </div>
                <div className="divider"></div>
                <div className="to">
                  <div className="d-flex align-items-center justify-content-center">
                    <Checkbox
                      value={valueTo}
                      className="ms-2 me-1"
                      onChange={(e) => {
                        setValueTo(e.value);
                      }}
                      label={""}
                    />
                    <DatePicker
                      id="to"
                      className="date-picker"
                      value={toDateSelector || null}
                      onChange={onChangeToDateSelect}
                      toggleButton={CalenderCustomToggleButton}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-end mb-3 ms-2 checkbox-label">
            <Checkbox
              checked={payWhenPaid}
              onChange={(e) => setPayWhenPaid(e.value)}
              label="Pay When Paid"
              className="checkbox-label"
            />
          </div>
        </div>

        <div className="d-flex align-items-end mb-2 gap-2">
          <Button
            className="common-btn border-0"
            svgIcon={arrowRotateCwIcon}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </div>
      </div>

      <Grid
        data={{
          data: list.data,
          total: list.total,
        }}
        {...dataState}
        className="flex-grow-1"
        style={{ height: "calc(100vh - 250px)", overflow: "hidden" }}
        onDataStateChange={dataStateChange}
        pageable={{
          pageSizes: GRID_PAGE_SIZES,
          buttonCount: 5,
        }}
        sortable={true}
        resizable={true}
        handleBody={HandleGridBody}
        showLoader={isLoading}
        loader={<GridLoader />}
      >
        <GridNoRecords>
          {isLoading ? (
            <div style={{ height: "110px" }}></div>
          ) : (
            list.data &&
            list.data.length === 0 &&
            "No unpaid disbursements available"
          )}
        </GridNoRecords>
        <GridColumn
          field="date"
          title="Date"
          width="140px"
          cells={{ data: DateFormatCell }}
        />
        <GridColumn field="postingType" title="Type" width="120px" />
        <GridColumn field="supplierName" title="Supplier" width="200px" />
        <GridColumn field="matterRef" title="Matter Ref" width="150px" />
        <GridColumn field="details" title="Details" width="250px" />
        <GridColumn
          field="vatAmount"
          title="VAT"
          width="120px"
          cells={{ data: AmountFormatCell }}
        />
        <GridColumn
          field="grossAmount"
          title="Gross"
          width="120px"
          cells={{ data: AmountFormatCell }}
        />
        <GridColumn
          field="amountPaid"
          title="Paid"
          width="120px"
          cells={{ data: AmountFormatCell }}
        />
        <GridColumn
          field="amountDue"
          title="Due"
          width="120px"
          cells={{ data: AmountFormatCell }}
        />
        <GridColumn
          title="Select"
          width="60px"
          cells={{ data: SelectCell }}
          headerCell={SelectHeaderCell}
          headerClassName="text-center align-middle px-1 py-0"
        />
      </Grid>

      <div className="d-flex justify-content-end mt-3">
        <Button
          className="common-btn"
          style={{ maxWidth: "130px" }}
          disabled={checkedList.length === 0}
          onClick={() => setShowPaySupplier(true)}
        >
          Add to Batch
        </Button>
      </div>

      {showPaySupplier && (
        <Dialog
          title="Pay Supplier"
          onClose={() => {
            setShowPaySupplier(false);
            handleRefresh();
          }}
          className="dialog-xxl"
        >
          <PaySupplier 
            preSelectedData={getSelectedData()}
            preSelectedSupplier={getSelectedSupplier()}
            onClose={() => {
              setShowPaySupplier(false);
              handleRefresh();
            }}
          />
        </Dialog>
      )}
    </div>
  );
};

export default CreditorsManagement;
